import { Type } from '@angular/core';
import { getJsonPath } from '@al/core';
import { ColumnDef } from '@al/design-patterns';
import { MapMarker, MapMarkerSerie, TimelineConfig } from '@al/ng-visualizations-components';
import { AlHtmlMatch } from './al-html-to-json.parser';


export interface AlBaseContentDescriptor {
    type: "markdown"|"json-viewer"|"map"|"table"|"image"|"timeline"|"error"|string;
    origin?: AlHtmlMatch;
}

export interface AlMarkdownContentDescriptor extends AlBaseContentDescriptor {
    markdown: string;
}

export interface AlMapContentDescriptor extends AlBaseContentDescriptor {
    markerSeries: MapMarkerSerie[];
    title: string;
    legendTitle?: string;
    showLegend?: boolean;
    showClusterCount?: boolean;
    showClusterCoverage?: boolean;
}

export interface AlTableContentDescriptor extends AlBaseContentDescriptor {
    title: string;
    columns: ColumnDef[];
    data: any[];
}

export interface AlImageContentDescriptor extends AlBaseContentDescriptor {
    source:string;
    title?:string;
    width?:string|number;
    height?:string|number;
}

export interface AlJsonContentDescriptor extends AlBaseContentDescriptor {
    data: any;
    expanded?: boolean;
}

export interface AlErrorContentDescriptor extends AlBaseContentDescriptor {
    error:string;
}
export interface AlTimelineContentDescriptor extends AlBaseContentDescriptor {
    timelineConfig: TimelineConfig;
}

export type AlAnyContentDescriptor = Partial<AlMarkdownContentDescriptor
                                             & AlJsonContentDescriptor
                                             & AlMapContentDescriptor
                                             & AlTableContentDescriptor
                                             & AlImageContentDescriptor
                                             & AlErrorContentDescriptor
                                             & AlTimelineContentDescriptor>;

export class AlContentInterpreter<ContextType=any> {
    constructor( public context:ContextType ) {
    }

    public async digest( match:AlHtmlMatch ):Promise<AlAnyContentDescriptor> {
        switch( match.name ) {
            case "c:json-viewer" :
                return this.digestJsonContent( match );

            case "c:map" :
                return this.digestMap( match );

            case "c:image" :
                return this.digestImage( match );

            case "c:table" :
                return this.digestTable( match );

            case "c:timeline":
                return this.digestTimeline( match );

        }
        return this.generateError( match, `There is no handler for content of type '${match.name}'` );
    }

    protected async digestJsonContent( match:AlHtmlMatch ) {
        let data:any;
        if ( 'source' in match.attributes ) {
            data = getJsonPath( this.context, match.attributes.source as string );
            if ( ! data ) {
                return this.generateError( match, `The data path '${match.attributes.source}' is not present in context data.` );
            }
        } else {
            try {
                data = JSON.parse( match.content );
            } catch( e ) {
                return this.generateError( match, `Some of the data in this content could not be displayed because it wasn't valid JSON: ${e.message}` );
            }
        }
        return {
            type: 'json-viewer',
            origin: match,
            data: data
        };
    }

    protected async digestTable( match:AlHtmlMatch ) {
        let columns = match.first("columns");
        let data:any[];
        if ( ! columns ) {
            return this.generateError( match, `Invalid c:table element does not have a 'columns' descriptor.` );
        }
        let columnDefinitions:ColumnDef[] = [];
        columns.children.forEach( columnDescr => {
            if ( columnDescr.name !== 'column' ) {
                this.generateError( match, `Invalid c:table element contains a rogue '${columnDescr.name}' element in its 'columns' definition` );
                return;
            }
            let column:ColumnDef = {
                header: columnDescr.attr( "header", "" ),
                field: columnDescr.attr( "source", "" )
            };
            columnDefinitions.push( column );
        } );

        let dataDescr = match.first("data");
        if ( ! dataDescr ) {
            return this.generateError( match, `Invalid c:table element does not have a 'data' descriptor.` );
        }

        if ( 'source' in dataDescr.attributes ) {
            data = getJsonPath<any[]>( this.context, dataDescr.attributes.source as string );
            if ( ! data || ! Array.isArray( data ) ) {
                return this.generateError( match, `c:table element refers to missing or non-array data at path '${dataDescr.attributes.source}' in context.` );
            }
        } else {
            try {
                data = JSON.parse( dataDescr.content );
                if ( ! data || ! Array.isArray( data ) ) {
                    return this.generateError( match, `c:table element's 'data' element does not contain an array.` );
                }
            } catch( e ) {
                return this.generateError( match, `c:table element's 'data' element contains malformed JSON: ${e.message}` );
            }
        }

        return {
            type: 'table',
            origin: match,
            columns: columnDefinitions,
            data: data
        };
    }

    protected async digestMap( match:AlHtmlMatch ) {
        let map:AlMapContentDescriptor = {
            type: 'map',
            origin: match,
            markerSeries: [],
            title: match.attr( 'title', "Untitled Map" ),
            legendTitle: match.attr( "legendTitle", null ),
            showLegend: match.attrSwitch( "showLegend", false ),
            showClusterCount: match.attrSwitch( 'showClusterCount', false ),
            showClusterCoverage:match.attrSwitch( 'showClusterCoverage', false )
        };

        let sourcePath = match.attr("source", null );
        if ( ! sourcePath ) {
            return this.generateError( match, `Failed to generate geo map: no source data specified` );
        }

        let descriptor = getJsonPath( this.context, sourcePath, null );
        if ( ! descriptor ) {
            return this.generateError( match, `Failed to generate map: data '${sourcePath}' was not available in source context` );
        }
        let latitude = descriptor.latitude || 0;
        let longitude = descriptor.longitude || 0;
        let title = `${descriptor.location_ip} (${descriptor.city}, ${descriptor.countryName})`;
        map.markerSeries.push( {
            name: title,
            color: 'red',
            clustered: true,
            markers: [
                {
                    coords: { 
                        lat: latitude,
                        lon: longitude
                    },
                    text: title
                }
            ]
        } );
        return map;
    }

    protected async digestImage( match:AlHtmlMatch ) {
        let source = match.attributes.source || match.attributes.src || undefined;
        if ( ! source ) {
            return this.generateError( match, `c:image element must have a 'source' property` );
        }
        let image:AlImageContentDescriptor = {
            type: "image",
            origin: match,
            source: source as string
        };
        if ( 'title' in match.attributes ) {
            image.title = match.attributes.title as string
        }
        return image;
    }

    protected async digestTimeline(match: AlHtmlMatch) {
        let sourcePath = match.attr("source", null);
        if (!sourcePath) {
            return this.generateError(match, `Failed to generate timeline: no source data specified`);
        }
        let descriptor = getJsonPath(this.context, sourcePath, null);
        if (!descriptor) {
            return this.generateError(match, `Failed to generate timeline: data '${sourcePath}' was not available in source context`);
        }
        let timelineConfig: TimelineConfig = descriptor;

        descriptor.data.forEach(item => {
            item.x = eval(item.x);
        });

        let timeline: AlTimelineContentDescriptor = {
            type: 'timeline',
            timelineConfig: timelineConfig
        };
        return timeline;

    }

    protected async generateError( match:AlHtmlMatch, error:string) {
        return {
            type: 'error',
            origin: match,
            error: error
        };
    }
}

